import React, { useState } from 'react';
import { ResponsiveContainer, ComposedChart, Line, ReferenceArea, ReferenceLine, XAxis, YAxis, CartesianGrid } from 'recharts';
import { FiChevronDown } from 'react-icons/fi';

// Mock data tracking the points of a 24-hour cycle matching the reference image layout
const chartData = [
  { time: '00:00', temp: 1.8 },
  { time: '02:00', temp: 2.2 },
  { time: '04:00', temp: 2.5 },
  { time: '06:00', temp: 1.2 },
  { time: '08:00', temp: 1.5 },
  { time: '10:00', temp: 2.1 },
  { time: '12:00', temp: 1.4 },
  { time: '14:00', temp: 1.6 },
  { time: '16:00', temp: 3.4 },
  { time: '18:00', temp: 1.9 },
  { time: '20:00', temp: 1.7 },
  { time: '22:00', temp: 0.9 },
  { time: '24:00', temp: 1.8 }
];

const TemperatureTrendToday = () => {
  const [timeRange, setTimeRange] = useState('24 Hours');

  // Bottom analytical metrics layout schema configuration mapping items exactly to the layout card
  const stats = [
    { label: 'Min Temp', value: '1.8°C', sub: '05:32' },
    { label: 'Avg Temp', value: '2.4°C', sub: '' },
    { label: 'Max Temp', value: '3.4°C', sub: '14:22' },
    { label: 'Time in Range', value: '100%', sub: '24h' },
    { label: 'High Temp Alerts', value: '0', sub: '0 events' },
    { label: 'Low Temp Alerts', value: '0', sub: '0 events' }
  ];

  return (
    <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.01)] p-5 font-sans select-none col-span-2">
      
      {/* Top Header Row Panel */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-bold text-gray-800 tracking-tight">
          Temperature Trend (Today)
        </h3>
        
        {/* Time Selector Dropdown Menu Pill */}
        <button className="flex items-center gap-1.5 border border-gray-200 hover:border-gray-300 rounded-lg px-2.5 py-1 text-xs font-bold text-gray-700 transition-colors cursor-pointer bg-white">
          <span>{timeRange}</span>
          <FiChevronDown className="w-3.5 h-3.5 text-gray-400 stroke-[2.5]" />
        </button>
      </div>

      {/* Graph Visual Legends Row Banner */}
      <div className="flex flex-wrap items-center justify-start gap-x-6 gap-y-2 mb-6 text-[11px] font-bold text-gray-500 pl-2">
        <div className="flex items-center gap-2">
          <span className="w-6 h-0.5 bg-[#0052FF] inline-block"></span>
          <span>Temperature</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-6 h-3 bg-[#EEF2FB] border border-dashed border-[#93C5FD]/40 rounded-xs inline-block"></span>
          <span>Safe Range (0°C – 5°C)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-5 h-0.5 border-t border-dashed border-[#EF4444] inline-block"></span>
          <span>High Alert (≥ 4.5°C)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-5 h-0.5 border-t border-dashed border-[#3B82F6] inline-block"></span>
          <span>Low Alert (≤ 0°C)</span>
        </div>
      </div>

      {/* Main Recharts Graph Core Visualization Box */}
      <div className="w-full h-64 pr-2 pl-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 5, left: -20, bottom: 0 }}>
            {/* Grid line formatting parameters matching screen layout markers */}
            <CartesianGrid vertical={false} stroke="#F3F4F6" strokeWidth={1} />
            
            <XAxis 
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9CA3AF', fontSize: 11, fontWeight: 600 }}
              dy={10}
              interval={2} // Mirrors the timestamp layout skips shown in the picture (00:00, 04:00, etc.)
            />
            
            <YAxis 
              domain={[-10, 10]}
              ticks={[10, 5, 0, -5, -10]}
              axisLine={false}
              tickLine={false}
              tickFormatter={(val) => `${val}°C`}
              tick={{ fill: '#9CA3AF', fontSize: 11, fontWeight: 600 }}
              dx={-5}
            />

            {/* 1. Safe Shading Boundary Zone Shaded Area Definition (0°C to 5°C) */}
            <ReferenceArea 
              y1={0} 
              y2={5} 
              fill="#EEF2FB" 
              fillOpacity={0.8} 
            />

            {/* 2. Red High Alert Horizontal Reference Line Indicator (4.5°C) */}
            <ReferenceLine 
              y={4.5} 
              stroke="#EF4444" 
              strokeWidth={1.2} 
              strokeDasharray="4 3" 
            />

            {/* 3. Blue Low Alert Horizontal Reference Line Indicator (0°C) */}
            <ReferenceLine 
              y={0} 
              stroke="#3B82F6" 
              strokeWidth={1} 
              strokeDasharray="4 3" 
              strokeOpacity={0.4}
            />

            {/* Core Temperature Monotone Chart Spline Line Layer */}
            <Line 
              type="monotone" 
              dataKey="temp" 
              stroke="#0052FF" 
              strokeWidth={2}
              dot={{ r: 1.5, fill: '#0052FF', strokeWidth: 0 }}
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom Row Grid Segment Panel: Analytics Metrics Summary Block */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 border-t border-gray-100 mt-8 pt-5 text-center">
        {stats.map((item, idx) => (
          <div 
            key={idx} 
            className="flex flex-col items-center justify-center p-1.5 space-y-1.5 border-r last:border-r-0 border-gray-100/70"
          >
            <span className="text-[11px] font-bold text-gray-400 block tracking-tight">
              {item.label}
            </span>
            <p className="text-[18px] font-black text-gray-900 leading-none">
              {item.value}
            </p>
            {item.sub ? (
              <span className="text-[10px] font-bold text-gray-400 block">
                {item.sub}
              </span>
            ) : (
              // Empty placeholder box row to lock height grid lines cleanly into symmetrical lines
              <span className="text-[10px] block opacity-0 select-none">-</span>
            )}
          </div>
        ))}
      </div>

    </div>
  );
};

export default TemperatureTrendToday;
